import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Button } from 'reactstrap';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { generateFieldId, setValue } from '../../../../components/InputField/helper';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import BusinessV2SectionedFieldStep from './components/BusinessV2SectionedFieldStep';
import DocumentFirstStepHead from './components/DocumentFirstStepHead';
import Entity from './components/EntityV2';
import User from './components/User';
import {
    collectVisibleFieldErrors,
    getFieldErrorValue,
    getNextStepV2,
    getVisibleWizardFieldOrder,
    orderFieldPathsByUiSections,
    V2_TYPE,
    WIZARD_NUM,
} from './helperV2';
import { runRecordValidators } from './recordValidators';
import FormValidationSummary from './FormValidationSummary';
import ReadOnlyFieldGrid from './ReadOnlyFieldGrid';
import LockedBanner from './LockedBanner';
import PersonDocumentsSlot from './PersonDocumentsSlot';
import { getLockState } from './applicationLock';
import useRequiredDocuments, { splitByPhase, DOC_STATUS, DOC_PHASE } from '../../../../hooks/useRequiredDocuments';
import DocumentRequirementList from './DocumentRequirementList';
import { VARIANT, variantClass } from '../../../../util/variant';
import BusinessV2FieldGrid from './BusinessV2FieldGrid';
import { useDocTypes } from '../../../../util/docTypeRuleMeta';
import { doGET } from '../../../../util/HttpUtil';
import './documentsVersion2.css';

const buildLocalId = () => `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const NEW_PERSON = {
    isAuthorizedUser: true,
    accessType: 'NoGroup',
    isShareHolder: false,
    isIndependentDirector: false,
};

const PERSON_ROLES = [
    {
        key: 'authorizedUser',
        label: 'Add Officer/Authorized User',
        capped: true,
        values: { isAuthorizedUser: true, accessType: 'NoGroup', isShareHolder: false, isIndependentDirector: false },
    },
    {
        key: 'shareholder',
        label: 'Add Beneficial Owner/Shareholder',
        values: { isShareHolder: true, isAuthorizedUser: false, isIndependentDirector: false },
    },
    {
        key: 'controlPerson',
        label: 'Add Control Prong',
        controlPersonOnly: true,
        values: { isControlPerson: true, isAuthorizedUser: false, isShareHolder: false, isIndependentDirector: false },
    },
    {
        key: 'director',
        label: 'Add Director/Manager',
        values: { isShareHolder: false, isAuthorizedUser: false, isIndependentDirector: true },
    },
];

const ADDRESS_LABELS = {
    REGISTERED: 'Registered Address of Business',
    MAILING: 'Mailing Address of Business',
    PRINCIPAL: 'Principal Place of Business',
};

const ADDRESS_FIELD_ORDER = ['street', 'buildingNumber', 'city', 'state', 'pinCode', 'country'];

// The registered/mailing address blocks render with ADDRESS_FIELD_ORDER (street above
// building); the principal address renders through the served config, which can order it
// building-first. Re-apply the shared order to the principal address sub-fields so all
// three blocks match. The addressList (registered/mailing) fields are filtered out of the
// field grid beforehand, so the only ADDRESS_FIELD_ORDER leaves left here are the principal's.
const addressFieldLeaf = (field) => {
    const path = String(field);
    const leaf = path.includes('.') ? path.slice(path.lastIndexOf('.') + 1) : path;
    return ADDRESS_FIELD_ORDER.includes(leaf) ? leaf : null;
};
const reorderPrincipalAddressFields = (fieldOrder) => {
    const addressFields = fieldOrder.filter((field) => addressFieldLeaf(field));
    if (addressFields.length < 2) return fieldOrder;
    const ordered = [...addressFields].sort(
        (a, b) => ADDRESS_FIELD_ORDER.indexOf(addressFieldLeaf(a)) - ADDRESS_FIELD_ORDER.indexOf(addressFieldLeaf(b)),
    );
    if (ordered.every((field, i) => field === addressFields[i])) return fieldOrder;
    const firstIndex = fieldOrder.findIndex((field) => addressFieldLeaf(field));
    const rest = fieldOrder.filter((field) => !addressFieldLeaf(field));
    rest.splice(firstIndex, 0, ...ordered);
    return rest;
};

const BusinessInformationVersion2 = ({
    asLabel,
    hideButtons,
    data,
    setData,
    handleUpdate,
    handlePrev,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    handleChange,
    applicationForm,
    editId,
    formId,
    editMode,
    jsonConfig,
    maxAuthorizedUsers = 11,
    pageNum = WIZARD_NUM.BusinessInformation,
    stepCount,
    stepLabel = 'Business Information',
    variant = VARIANT.V2,
    lastSavedAt,
}) => {
    const { showError } = useContext(DialogContext);
    const applicationType = Number(applicationForm?.type);
    const wizardContent = useMemo(
        () => jsonConfig?.wizardContent?.[wizard] || {},
        [jsonConfig, wizard],
    );
    const lock = useMemo(() => getLockState(applicationForm, jsonConfig), [applicationForm, jsonConfig]);
    const readOnly = asLabel || lock.readOnlyAll;

    const visibleFieldOrder = useMemo(() => getVisibleWizardFieldOrder({
        fields: iPlusFields,
        wizard,
        data,
        type: applicationType,
    }), [iPlusFields, wizard, data, applicationType]);

    // Broker (GMA) Business Plus applications are Plus-only and must be non-financial-institution.
    // The document-first config wires the Prime-routing rule to `isFinancialInstitution`
    // (businessTypeConfig.field), so instead of the Yes/No radio — whose "Yes" would try to route a
    // broker app to Prime — these apps get the same "not a Fintech / MSB" attestation the legacy GMA
    // form used (see BusinessTypeV2). Confirming it pins the field to the Plus-eligible value
    // (isFinancialInstitution = false). Scoped to the wizard that actually owns the field.
    const isBrokerMode = Boolean(applicationForm?.brokerId);
    const businessTypeField = jsonConfig?.businessTypeConfig?.field;
    const plusEligibleValue = jsonConfig?.businessTypeConfig?.plusEligibleValue;
    const ownsNotMsbAttestation = isBrokerMode
        && Boolean(businessTypeField)
        && visibleFieldOrder.includes(businessTypeField);
    const notMsbConfirmed = ownsNotMsbAttestation && data?.[businessTypeField] === plusEligibleValue;
    const toggleNotMsb = (checked) => {
        if (readOnly) return;
        setErrors((prev) => ({ ...(prev || {}), [businessTypeField]: '' }));
        handleChange((prev) => ({
            ...prev,
            [businessTypeField]: checked ? plusEligibleValue : undefined,
        }));
    };

    const individuals = useMemo(
        () => (Array.isArray(data?.individuals) ? data.individuals : []),
        [data?.individuals],
    );
    const entities = useMemo(
        () => (Array.isArray(data?.entities) ? data.entities : []),
        [data?.entities],
    );

    const recordId = editId || formId;
    // Any change to a saved individual's data can change the documents the backend requires of them.
    // This signature feeds the autosave-gated refetch below; it deliberately drives no fetch on its
    // own, so the list is only refreshed once the change has been persisted.
    const individualsDocSignature = useMemo(
        () => (individuals || [])
            .filter((person) => person?._id)
            .map((person) => {
                const { _localId, _isNew, documents, attachments, ...rest } = person || {};
                return `${person._id}:${JSON.stringify(rest)}`;
            })
            .join('|'),
        [individuals],
    );
    const {
        business: businessRequirements,
        individuals: personRequirements,
        refetch: refetchPersonRequirements,
    } = useRequiredDocuments(recordId, { individuals });
    const { docTypeMap } = useDocTypes();
    const allPersonDocuments = useMemo(
        () => personRequirements.flatMap((group) => group.documents),
        [personRequirements],
    );

    const businessFinalDocuments = useMemo(
        () => splitByPhase(businessRequirements).final,
        [businessRequirements],
    );

    const ownedSectionIds = useMemo(() => new Set(
        (Array.isArray(wizardContent?.uiSections) ? wizardContent.uiSections : [])
            .map((section) => section?.id)
            .filter(Boolean),
    ), [wizardContent]);
    const hasBusinessDocumentsSection = ownedSectionIds.has('businessDocuments');

    const ownsSection = useMemo(() => {
        const entries = Object.entries(jsonConfig?.wizardContent || {});
        const declaresSection = (content, id) => (
            (Array.isArray(content?.uiSections) ? content.uiSections : [])
                .some((section) => section?.id === id)
        );
        return (sectionId) => (
            ownedSectionIds.has(sectionId)
            || !entries.some(([, content]) => declaresSection(content, sectionId))
        );
    }, [jsonConfig, ownedSectionIds]);
    const ownsPeopleSection = ownsSection('people');
    const ownsEntitiesSection = ownsSection('entities');
    const ownsAddressListSection = ownedSectionIds.has('addressList');

    // Refetch a person's required documents only after their edit has autosaved: the backend
    // recomputes the list from the stored data, so fetching before the update lands returns a stale
    // list. `lastSavedAt` ticks when a save settles; the signature ref keeps this off the local
    // change so it never races the autosave.
    const docSignatureRef = useRef(individualsDocSignature);
    docSignatureRef.current = individualsDocSignature;
    const refetchedDocSignatureRef = useRef(individualsDocSignature);
    useEffect(() => {
        if (!lastSavedAt || !ownsPeopleSection) return;
        if (docSignatureRef.current === refetchedDocSignatureRef.current) return;
        refetchedDocSignatureRef.current = docSignatureRef.current;
        refetchPersonRequirements();
    }, [lastSavedAt, ownsPeopleSection, refetchPersonRequirements]);

    const ownsBusinessDocumentsFallback = useMemo(() => {
        const entries = Object.entries(jsonConfig?.wizardContent || {});
        const declaresSection = (content, id) => (
            (Array.isArray(content?.uiSections) ? content.uiSections : [])
                .some((section) => section?.id === id)
        );
        if (entries.some(([, content]) => declaresSection(content, 'businessDocuments'))) return false;
        const peopleOwner = entries.find(([, content]) => declaresSection(content, 'people'))?.[0];
        return !peopleOwner || peopleOwner === wizard;
    }, [jsonConfig, wizard]);
    const ownsBusinessDocuments = hasBusinessDocumentsSection || ownsBusinessDocumentsFallback;

    const ownerDisplayName = useMemo(() => {
        const map = new Map();
        personRequirements.forEach((group) => {
            if (group?.ownerId) map.set(group.ownerId, group.displayName);
        });
        return map;
    }, [personRequirements]);

    // Every row the required-documents API returns is a mandatory upload; a row is satisfied only
    // when its status is UPLOADED. Collect the still-missing ones for the document sets THIS step
    // actually renders (per-person docs when it owns the people section, business docs when it owns
    // the business-documents slot). Scoping by what the step renders keeps the rule correct for both
    // the broker split (People step) and the console collapsed Business Information step, and leaves
    // the Documents step's start-doc gate untouched. This in-memory list drives nothing on its own:
    // the Continue gate re-reads the authoritative list from the server at submit time (see
    // firstMissingRequiredDocument) because this list can briefly lag a just-finished upload. It is
    // only the fallback used when that server read fails.
    const missingRequiredDocuments = useMemo(() => {
        const missing = [];
        if (ownsPeopleSection) {
            allPersonDocuments.forEach((row) => {
                if (row?.status !== DOC_STATUS.UPLOADED) missing.push(row);
            });
        }
        if (ownsBusinessDocuments) {
            businessFinalDocuments.forEach((row) => {
                if (row?.status !== DOC_STATUS.UPLOADED) missing.push(row);
            });
        }
        return missing;
    }, [ownsPeopleSection, allPersonDocuments, ownsBusinessDocuments, businessFinalDocuments]);

    const slotCoveredFieldOrder = useMemo(
        () => reorderPrincipalAddressFields(visibleFieldOrder.filter((field) => {
            const path = String(field);
            if (ownsPeopleSection && /^individuals\[/.test(path)) return false;
            if (ownsEntitiesSection && /^entities\[/.test(path)) return false;
            if (ownsAddressListSection && /^addressList\[/.test(path)) return false;
            // The FI question is replaced by the not-MSB attestation for broker apps, so keep it
            // out of the field grid (it would otherwise render as the Yes/No radio).
            if (ownsNotMsbAttestation && field === businessTypeField) return false;
            return true;
        })),
        [visibleFieldOrder, ownsPeopleSection, ownsEntitiesSection, ownsAddressListSection, ownsNotMsbAttestation, businessTypeField],
    );

    const onFieldError = (field, errorMsg) => setErrors((prev) => ({ ...(prev || {}), [field]: errorMsg }));

    const mutateArray = (key, next) => {
        if (readOnly) return;
        setData((prev) => setValue({ ...prev }, key, next(Array.isArray(prev?.[key]) ? prev[key] : []), false));
    };

    const addPersonWithRole = (role) => {
        if (role?.capped) {
            const authorized = individuals.filter((i) => i?.isAuthorizedUser).length;
            if (authorized >= maxAuthorizedUsers) {
                showError(`Maximum ${maxAuthorizedUsers} authorized user${maxAuthorizedUsers === 1 ? '' : 's'} allowed.`);
                return;
            }
        }
        mutateArray('individuals', (current) => [
            ...current,
            { ...NEW_PERSON, ...(role?.values || {}), _isNew: true, _localId: buildLocalId() },
        ]);
    };

    const visiblePersonRoles = PERSON_ROLES.filter(
        (role) => !role.controlPersonOnly || applicationType === V2_TYPE.PLUS,
    );

    const removePerson = (index) => {
        if (index === 0) return;
        mutateArray('individuals', (current) => current.filter((_item, i) => i !== index));
    };

    const addEntity = () => mutateArray('entities', (current) => (
        [...current, { _isNew: true, _localId: buildLocalId() }]
    ));

    const removeEntity = (index) => mutateArray('entities', (current) => (
        current.filter((_item, i) => i !== index)
    ));

    // "Does your organization have any parent, subsidiary and/or affiliate?" = Yes seeds one entity
    // (the first is mandatory and non-removable, see entitiesSlot); = No clears the list.
    useEffect(() => {
        if (!ownsEntitiesSection || readOnly) return;
        setData((prev) => {
            const list = Array.isArray(prev?.entities) ? prev.entities : [];
            if (prev?.hasEntity === true && list.length === 0) {
                return setValue({ ...prev }, 'entities', [{ _isNew: true, _localId: buildLocalId() }], false);
            }
            if (prev?.hasEntity === false && list.length > 0) {
                return setValue({ ...prev }, 'entities', [], false);
            }
            return prev;
        });
    }, [data?.hasEntity, ownsEntitiesSection, readOnly, setData]);

    const sameAsPrincipalFlag = (type) => (
        type === 'MAILING' ? 'isMailingAddressSame' : 'isRegisteredAddressSame'
    );

    const toggleSameAsPrincipal = (index, type) => {
        const key = sameAsPrincipalFlag(type);
        handleChange((prev) => {
            const nextChecked = !prev?.[key];
            if (!nextChecked) return setValue({ ...prev }, key, false, false);
            const principal = prev?.address || {};
            let next = setValue({ ...prev }, key, true, false);
            ADDRESS_FIELD_ORDER.forEach((leaf) => {
                next = setValue(next, `addressList[].${leaf}`, principal?.[leaf] ?? '', false, '', index);
            });
            next = setValue(next, 'addressList[].hidePinCode', principal?.hidePinCode ?? false, false, '', index);
            return next;
        });
    };

    const addressListSlot = () => {
        const list = Array.isArray(data?.addressList) ? data.addressList : [];
        if (!list.length) return null;
        return (
            <div className="docfirst__repeat">
                {list.map((entry, index) => {
                    const canSameAsPrincipal = entry?.type === 'MAILING' || entry?.type === 'REGISTERED';
                    const sameKey = sameAsPrincipalFlag(entry?.type);
                    const sameChecked = Boolean(data?.[sameKey]);
                    return (
                        <div className="docfirst__repeat-item" key={entry?._id || entry?._localId || index}>
                            <div className="docfirst__repeat-head">
                                <span className="docfirst__repeat-title">
                                    {ADDRESS_LABELS[entry?.type] || `Address ${index + 1}`}
                                </span>
                                {canSameAsPrincipal && !readOnly && (
                                    <label className="docfirst__same-as">
                                        <input
                                            type="checkbox"
                                            checked={sameChecked}
                                            onChange={() => toggleSameAsPrincipal(index, entry?.type)}
                                            data-testid={`business-v2-address-${index}-same-as-principal`}
                                        />
                                        <span>Same as the principal place of business</span>
                                    </label>
                                )}
                            </div>
                            <BusinessV2FieldGrid
                                fieldOrder={ADDRESS_FIELD_ORDER.map((leaf) => `addressList[${index}].${leaf}`)}
                                fieldMap={cyclosFields}
                                data={data}
                                asLabel={readOnly}
                                handleChange={canSameAsPrincipal ? ((value) => handleChange((prev) => {
                                    const nextData = typeof value === 'function' ? value(prev) : value;
                                    return prev?.[sameKey] ? setValue({ ...nextData }, sameKey, false, false) : nextData;
                                })) : handleChange}
                                hasError={hasError}
                                onFieldError={onFieldError}
                                dataTestId={`business-v2-address-${index}`}
                                variant={variant}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    const peopleSlot = () => (
        <div className="docfirst__repeat">
            {individuals.map((person, index) => (
                <div className="docfirst__repeat-item" key={person?._id || person?._localId || index}>
                    <div className="docfirst__repeat-head">
                        <span className="docfirst__repeat-title">
                            {index === 0 ? 'Primary user' : `Person ${index + 1}`}
                        </span>
                        {index !== 0 && !readOnly && (
                            <Button color="link" className="docfirst__repeat-remove" onClick={() => removePerson(index)}>
                                <FaTimes size={11} /> Remove
                            </Button>
                        )}
                    </div>
                    <User
                        asLabel={readOnly}
                        data={data}
                        handleChange={handleChange}
                        hasError={hasError}
                        index={index}
                        cyclosFields={cyclosFields}
                        setErrors={setErrors}
                        iPlusFields={iPlusFields}
                        applicationForm={applicationForm}
                        documentsSlot={({ ownerId }) => (
                            <PersonDocumentsSlot
                                ownerId={ownerId}
                                displayName={[person?.firstName, person?.lastName].filter(Boolean).join(' ')}
                                documents={allPersonDocuments}
                                recordId={recordId}
                                asLabel={readOnly}
                                docTypeMap={docTypeMap}
                                onDocumentsChanged={refetchPersonRequirements}
                            />
                        )}
                    />
                </div>
            ))}
            {!readOnly && (
                <div className="docfirst__repeat-actions">
                    {visiblePersonRoles.map((role) => (
                        <Button
                            key={role.key}
                            color="link"
                            className="docfirst__repeat-add"
                            data-testid={`business-v2-add-${role.key}`}
                            onClick={() => addPersonWithRole(role)}
                        >
                            <FaPlus size={11} /> {role.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );

    const businessDocumentsSlot = () => {
        if (!businessFinalDocuments.length) return null;
        return (
            <div className="docfirst__person-docs">
                <div className="docfirst__person-docs-head">Documents for the business</div>
                <DocumentRequirementList
                    documents={businessFinalDocuments}
                    recordId={recordId}
                    asLabel={readOnly}
                    docTypeMap={docTypeMap}
                    onDocumentsChanged={refetchPersonRequirements}
                />
                <div className="docfirst__person-docs-note">
                    Each document is uploaded once here and saved to the application.
                </div>
            </div>
        );
    };

    const entitiesSlot = () => (
        data?.hasEntity !== true ? null : (
        <div className="docfirst__repeat">
            {(entities.length ? entities : [{}]).map((entity, index) => (
                <div className="docfirst__repeat-item" key={entity?._id || entity?._localId || index}>
                    <div className="docfirst__repeat-head">
                        <span className="docfirst__repeat-title">{`Entity ${index + 1}`}</span>
                        {/* Prod parity: the first/only entity is mandatory, so Remove appears only once a
                            second entity has been added (never letting the list drop below one). */}
                        {!readOnly && entities.length > 1 && (
                            <Button color="link" className="docfirst__repeat-remove" onClick={() => removeEntity(index)}>
                                <FaTimes size={11} /> Remove
                            </Button>
                        )}
                    </div>
                    <Entity
                        asLabel={readOnly}
                        data={data}
                        handleChange={handleChange}
                        hasError={hasError}
                        index={index}
                        cyclosFields={cyclosFields}
                        setErrors={setErrors}
                    />
                </div>
            ))}
            {!readOnly && (
                <Button color="link" className="docfirst__repeat-add" onClick={addEntity}>
                    <FaPlus size={11} /> Add another entity
                </Button>
            )}
        </div>
        )
    );

    const focusFieldPath = (fieldPath) => {
        if (!fieldPath || typeof document === 'undefined') return;
        const arrayIndexMatch = String(fieldPath).match(/\[(\d+)\]/);
        const arrayIndexing = arrayIndexMatch ? Number(arrayIndexMatch[1]) : null;
        const templateKey = String(fieldPath).replace(/\[\d+\]/g, '[]');
        const jsonField = cyclosFields?.[fieldPath] || cyclosFields?.[templateKey];
        if (!jsonField) return;
        const element = document.getElementById(generateFieldId(jsonField, arrayIndexing, data));
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus?.();
    };

    // Scroll the first unuploaded required document into view. The testid mirrors the one
    // DocumentRequirementList stamps on each upload control: `doc-<type>[-<ownerId>]`.
    const focusDocument = (row) => {
        if (!row || typeof document === 'undefined') return;
        const testId = `doc-${row.documentType}${row.ownerId ? `-${row.ownerId}` : ''}`;
        const element = document.querySelector(`[data-testid="${testId}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const describeMissingDocument = (row) => {
        const who = row?.ownerId
            ? (ownerDisplayName.get(row.ownerId) || 'this person')
            : 'the business';
        const name = row?.label || 'required document';
        return `Please upload the required document “${name}” for ${who} before continuing.`;
    };

    const cmpPos = (a, b) => (a[0] - b[0]) || (a[1] - b[1]) || (a[2] - b[2]) || String(a[3]).localeCompare(String(b[3]));

    // Build this step's blocking errors WITHOUT reporting them, so handleSubmit can merge in the
    // (async, authoritative) missing-document rows at the right on-screen position. Each error carries a
    // [group, instance, field, tiebreak] position so fields and documents sort into one on-screen order.
    const buildGateErrors = () => {
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: applicationType,
        });
        const merged = { ...(errors || {}), ...fieldErrors };

        // Broker apps attest they are not a Fintech/MSB instead of answering the FI radio; the field
        // is pinned to the Plus-eligible value only once confirmed. It is business-level, so it gates
        // before everything else.
        const attestationFailed = ownsNotMsbAttestation && data?.[businessTypeField] !== plusEligibleValue;

        // The gate reports/focuses the FIRST on-screen error, so its order MUST match what the user
        // sees: sections in uiSections order, config order within each section (what resolveSections
        // renders), slot groups (people before entities) by section too, and every address block
        // normalised to street→building→…. orderFieldPathsByUiSections is the shared helper that does
        // all of this, so ordering stays consistent with the other V2 steps. slotCoveredFieldOrder is
        // the grid's list (slots filtered out); the slot groups (addressList/individuals/entities) are
        // ordered the same way and appended so the gate still covers them.
        const renderedFlatOrder = orderFieldPathsByUiSections(slotCoveredFieldOrder, wizardContent?.uiSections);
        const slotLeftovers = visibleFieldOrder.filter((fieldPath) => !renderedFlatOrder.includes(fieldPath));
        const templateOrder = [
            ...renderedFlatOrder,
            ...orderFieldPathsByUiSections(slotLeftovers, wizardContent?.uiSections),
        ];
        // `merged` is keyed by RESOLVED paths (individuals[0].x, addressList[1].street); below we map
        // each errored key to its TEMPLATE path to look up the on-screen position, then order by
        // [group, instance, field] so the toast/focus land on the first invalid field a user sees.
        const toTemplatePath = (key) => String(key).replace(/\[\d+\]/g, '[]');
        const baseOf = (templatePath) => {
            const i = templatePath.indexOf('[]');
            return i === -1 ? templatePath : templatePath.slice(0, i);
        };
        // templateIndexOf keys by TEMPLATE path (templateOrder holds resolved array paths like
        // entities[0].name). groupStartIndex is where each repeating block (addressList/individuals/
        // entities) first appears, so errors walk block-by-block in on-screen order (whole Registered
        // block, then Mailing; person 1, then person 2) instead of interleaving them field-by-field.
        const templateIndexOf = new Map();
        const groupStartIndex = new Map();
        templateOrder.forEach((path, index) => {
            const templatePath = toTemplatePath(path);
            if (!templateIndexOf.has(templatePath)) templateIndexOf.set(templatePath, index);
            const base = baseOf(templatePath);
            if (!groupStartIndex.has(base)) groupStartIndex.set(base, index);
        });
        const instanceIndexOf = (key) => {
            const match = String(key).match(/\[(\d+)\]/);
            return match ? Number(match[1]) : -1;
        };
        const idxOrMax = (map, k) => (map.has(k) ? map.get(k) : Number.MAX_SAFE_INTEGER);
        const posOfField = (key) => [
            idxOrMax(groupStartIndex, baseOf(toTemplatePath(key))),
            instanceIndexOf(key),
            idxOrMax(templateIndexOf, toTemplatePath(key)),
            key,
        ];
        const fieldItems = Object.keys(merged)
            // Only gate on fields actually on this step (their template is in the render order).
            .filter((key) => getFieldErrorValue(merged, key) && templateIndexOf.has(toTemplatePath(key)))
            .map((key) => ({ type: 'field', key, message: getFieldErrorValue(merged, key), pos: posOfField(key) }));

        // A per-person document sits right after that person's fields (field index = MAX), so the order
        // reads person 1 fields → person 1 document → person 2 fields → … A business document has no
        // person and renders after the whole form, so it sorts past every field group.
        const individualsStart = idxOrMax(groupStartIndex, 'individuals');
        const individualIndexById = new Map(
            (Array.isArray(data?.individuals) ? data.individuals : []).map((person, index) => [person?._id, index]),
        );
        const posOfPersonDoc = (ownerId) => [
            individualsStart,
            individualIndexById.has(ownerId) ? individualIndexById.get(ownerId) : Number.MAX_SAFE_INTEGER,
            Number.MAX_SAFE_INTEGER,
            `doc:${ownerId}`,
        ];
        const posOfBusinessDoc = () => [templateOrder.length, 0, 0, 'doc:business'];

        return { merged, attestationFailed, fieldItems, posOfPersonDoc, posOfBusinessDoc };
    };

    // A required document rendered on this step must be uploaded before the step can be left. The
    // in-memory requirements list can briefly lag a just-finished upload (the per-document refetch is
    // async), so deciding off it would wrongly block an applicant who has in fact uploaded everything.
    // Re-read the authoritative list from the server at submit time and decide on the fresh response;
    // fall back to the in-memory list only if that read fails. Rows are grouped exactly as
    // useRequiredDocuments groups them: business = forBusiness || no ownerId; per-person = the rest.
    const collectMissingFromRows = (docs) => (Array.isArray(docs) ? docs : []).filter((row) => {
        if (!row?.documentType || row.status === DOC_STATUS.UPLOADED) return false;
        const isBusiness = row.forBusiness || !row.ownerId;
        if (!isBusiness) return ownsPeopleSection;
        return ownsBusinessDocuments && row.phase === DOC_PHASE.FINAL;
    });

    const listMissingRequiredDocuments = async () => {
        if (!ownsPeopleSection && !ownsBusinessDocuments) return [];
        try {
            const response = await doGET(`/api/application/document/required/${recordId}`);
            if (response?.status === 200 && Array.isArray(response?.data?.documents)) {
                return collectMissingFromRows(response.data.documents);
            }
        } catch (e) {
            // fall back to the in-memory list below
        }
        return missingRequiredDocuments;
    };

    const submittingRef = useRef(false);
    const handleSubmit = async () => {
        if (readOnly || submittingRef.current) return undefined;
        submittingRef.current = true;
        try {
            const gate = buildGateErrors();
            setErrors(gate.merged);

            if (gate.attestationFailed) {
                setHasError(true);
                showError('Please confirm you are not a Fintech or Money Service Business to continue.');
                return undefined;
            }

            // Interleave the authoritative missing-document rows with the field errors so the toast and
            // focus follow the on-screen order: person 1 fields → person 1 document → person 2 fields →
            // … → entities → business documents. The server list is authoritative (the in-memory list can
            // lag a just-finished upload); it falls back to the in-memory list only if the read fails.
            const missingDocs = await listMissingRequiredDocuments();
            const docItems = missingDocs.map((row) => ({
                type: 'doc',
                row,
                pos: (row.forBusiness || !row.ownerId) ? gate.posOfBusinessDoc() : gate.posOfPersonDoc(row.ownerId),
            }));

            const ordered = [...gate.fieldItems, ...docItems].sort((a, b) => cmpPos(a.pos, b.pos));
            if (ordered.length) {
                const first = ordered[0];
                setHasError(true);
                if (first.type === 'doc') {
                    showError(describeMissingDocument(first.row));
                    focusDocument(first.row);
                } else {
                    showError(first.message);
                    focusFieldPath(first.key);
                }
                return undefined;
            }

            // People-level rules (ownership totals, control prong, duplicates, shareholder %, US-SSN) run
            // only once every field and document on the step is satisfied.
            const { hasErrors, recordLevel, fieldLevel } = ownsPeopleSection
                ? runRecordValidators(data)
                : { hasErrors: false, recordLevel: [], fieldLevel: [] };
            if (hasErrors) {
                if (fieldLevel.length) {
                    setErrors((previous) => ({
                        ...(previous || {}),
                        ...Object.fromEntries(fieldLevel.filter((item) => item.key).map((item) => [item.key, item.message])),
                    }));
                }
                setHasError(true);
                showError((recordLevel[0] || fieldLevel[0])?.message || 'Please review the highlighted details.');
                if (!recordLevel.length) focusFieldPath(fieldLevel[0]?.key);
                return undefined;
            }

            setErrors({});
            setHasError(false);
            const nextStep = applicationForm?.getNextStepForForm?.({ wizard, data, editMode })
                || getNextStepV2(wizard, applicationType, data, jsonConfig, { editMode });
            return await handleUpdate({ nextStep });
        } finally {
            submittingRef.current = false;
        }
    };

    // Rendered as the lead item of the sections stack so it is the first question on the step.
    const notMsbAttestation = ownsNotMsbAttestation ? (
        <div className="docfirst__attestation" data-testid="business-v2-not-msb">
            <p className="docfirst__attestation-intro">
                FV Bank managed accounts are available to non-financial businesses. Please confirm the declaration below to continue.
            </p>
            <label className={`docfirst__attestation-check ${readOnly ? 'is-disabled' : ''}`.trim()}>
                <input
                    type="checkbox"
                    checked={notMsbConfirmed}
                    disabled={readOnly}
                    onChange={(event) => toggleNotMsb(event?.target?.checked)}
                    data-testid="business-v2-not-msb-confirm"
                />
                <span>Confirm you are not a Fintech or Money Service Business company!</span>
            </label>
            {hasError && !notMsbConfirmed ? (
                <p className="docfirst__attestation-error">
                    Please confirm you are not a Fintech or Money Service Business to continue.
                </p>
            ) : null}
        </div>
    ) : null;

    return (
        <div

            className={`business-v2-step-shell docfirst ${variantClass(variant)}`.trim()}
            data-testid="business-v2-business-information-version2"
        >
            {}
            <DocumentFirstStepHead
                stepIndex={pageNum}
                stepCount={stepCount}
                stepLabel={stepLabel}

                title={wizardContent?.hero?.title || stepLabel || 'Business Information'}
                intro={wizardContent?.hero?.description || ''}
                dataTestId="business-v2-business-information-version2-head"
            />
            <LockedBanner show={lock.readOnlyAll} variant={variant} />
            {}
            <FormValidationSummary
                data={data}
                show={Boolean(hasError) && ownsPeopleSection}
                variant={variant}
                onSelect={(item) => focusFieldPath(item?.key)}
            />

            {lock.readOnlyAll ? (
                <>
                    {notMsbAttestation}
                    <ReadOnlyFieldGrid
                        fieldOrder={slotCoveredFieldOrder}
                        fieldMap={cyclosFields}
                        data={data}
                        variant={variant}
                    />
                    {}
                    {ownsPeopleSection ? peopleSlot() : null}
                    {ownsEntitiesSection ? entitiesSlot() : null}
                    {hasBusinessDocumentsSection || ownsBusinessDocumentsFallback
                        ? businessDocumentsSlot()
                        : null}
                </>
            ) : (
                <BusinessV2SectionedFieldStep
                    title="Business Information"
                    titleIcon="default"
                    hideHeader
                    showSectionNavigator={false}
                    wizardContent={wizardContent}

                    visibleFieldOrder={slotCoveredFieldOrder}
                    fieldMap={cyclosFields}
                    data={data}
                    asLabel={readOnly}
                    handleChange={handleChange}
                    hasError={hasError}
                    onFieldError={onFieldError}
                    dataTestIdPrefix="business-v2-business-information-version2-sections"
                    sectionSlots={{
                        people: peopleSlot,
                        entities: entitiesSlot,
                        addressList: addressListSlot,
                        businessDocuments: businessDocumentsSlot,
                    }}
                    leadContent={notMsbAttestation}
                    variant={variant}
                />
            )}

            {}
            {!hasBusinessDocumentsSection
                && ownsBusinessDocumentsFallback
                && !lock.readOnlyAll
                && businessDocumentsSlot()}

            <div className="business-v2-step-actions">
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={handlePrev}
                    step={pageNum}
                    pageNum={pageNum}
                    showPageNum={false}
                    nextText={variant === VARIANT.V2 ? 'Continue' : undefined}
                    hideSubmitButton={hideButtons}
                    isDisabled={readOnly}
                    asLabel={readOnly}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default BusinessInformationVersion2;

